import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Mirror of the backend password policy (backend utils/passwordPolicy.js,
// pentest V_006). The server is authoritative for /registration and
// /completeActivation; this copy lets the forms refuse early. Profile password
// changes and the reset-link page go straight to Firebase (updatePassword /
// confirmPasswordReset), so there this is the only check until the Identity
// Platform password policy is enabled. Keep in sync with the backend and the
// admin / betAdmin copies.

export const MIN_PASSWORD_LENGTH = 8;

const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password12', 'password123', 'passw0rd', 'p@ssw0rd',
  'p@ssword', 'motdepasse', 'motdepasse1', 'motdepasse123', 'azertyui',
  'azertyuiop', 'azerty123', 'azerty1234', 'qwertyui', 'qwertyuiop', 'qwerty123',
  'qwerty1234', 'iloveyou', 'iloveyou1', 'jetaime1', 'jetaime123', 'bonjour1',
  'bonjour123', 'soleil123', 'doudou123', 'chouchou', 'princesse', 'football',
  'football1', 'marseille', 'tunisie1', 'tunisia1', 'welcome1', 'welcome123',
  'letmein1', 'sunshine', 'baseball', 'superman', 'trustno1', 'whatever',
  'starwars', 'dragon123', 'monkey123', 'master123', 'shadow123', 'admin123',
  'admin1234', 'administrator', 'superadmin', 'goatadmin', 'branchadmin',
  'racebet', 'racebet1', 'racebet123', 'racebet365', 'forza281113', 'tabassi1',
  'tabbassi', 'changeme', 'changeme1', 'default1', '1q2w3e4r', '1qaz2wsx',
  'zaq12wsx', 'q1w2e3r4', 'a1b2c3d4', 'abc12345', 'abcd1234', 'aa123456',
  'qwe12345', 'asdfghjk', 'zxcvbnm1', '147258369', '159753258', '789456123',
  '741852963', '123qweasd', '12qwaszx',
]);

const SEQUENCE_ALPHABETS = [
  '0123456789012345678901234567890',
  'abcdefghijklmnopqrstuvwxyzabcdefghijklmnopqrstuvwxyz',
  'azertyuiopqsdfghjklmwxcvbn',
  'qwertyuiopasdfghjklzxcvbnm',
];

export type PasswordWeakness = 'NOT_A_STRING' | 'TOO_SHORT' | 'COMMON' | 'PATTERN' | 'CONTAINS_IDENTITY';

function isRepeatedOrCycled(pw: string): boolean {
  return /^(.{1,4})\1+.{0,3}$/.test(pw) && new Set(pw).size <= 4;
}

function isSequence(pw: string): boolean {
  return SEQUENCE_ALPHABETS.some((alphabet) => {
    const reversed = alphabet.split('').reverse().join('');
    return alphabet.includes(pw) || reversed.includes(pw);
  });
}

function containsIdentity(pw: string, identity?: string | null): boolean {
  if (!identity || typeof identity !== 'string') { return false; }
  const needle = identity.trim().toLowerCase();
  return needle.length >= 3 && pw.includes(needle);
}

export function passwordWeakness(password: unknown, identity: { username?: string | null, email?: string | null } = {}): PasswordWeakness | null {
  if (typeof password !== 'string') { return 'NOT_A_STRING'; }
  if (password.length < MIN_PASSWORD_LENGTH) { return 'TOO_SHORT'; }
  const pw = password.toLowerCase();
  if (COMMON_PASSWORDS.has(pw)) { return 'COMMON'; }
  if (isRepeatedOrCycled(pw) || isSequence(pw)) { return 'PATTERN'; }
  const emailLocalPart = typeof identity.email === 'string' ? identity.email.split('@')[0] : null;
  if (containsIdentity(pw, identity.username) || containsIdentity(pw, emailLocalPart)) { return 'CONTAINS_IDENTITY'; }
  return null;
}

/**
 * Form validator: `{ passwordPolicy: reason }` when the value breaks the
 * policy. Empty values are left to Validators.required. `usernameOf` reads the
 * username the password must not contain (e.g. a sibling control).
 */
export function passwordPolicyValidator(usernameOf?: () => string | null | undefined): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.value === null || control.value === undefined || control.value === '') { return null; }
    const reason = passwordWeakness(control.value, { username: usernameOf ? usernameOf() : null });
    return reason ? { passwordPolicy: reason } : null;
  };
}

// No 0/O, 1/l/I: a counter password is read out or typed by hand.
const GENERATED_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

/** Cryptographically random password that always satisfies the policy. */
export function generatePassword(length = 10): string {
  let out = '';
  do {
    const bytes = new Uint32Array(length);
    crypto.getRandomValues(bytes);
    out = Array.from(bytes, (b) => GENERATED_ALPHABET[b % GENERATED_ALPHABET.length]).join('');
  } while (passwordWeakness(out) !== null);
  return out;
}

/** True when an HTTP error body is the backend's weak-password refusal (error code 198 or the DTO length floor). */
export function isWeakPasswordError(body: any): boolean {
  if (!body) { return false; }
  return body.errorCode === 198 || (body.statusCode === 400 && /password/i.test(String(body.message || '')));
}
